import React, { useEffect, useRef } from 'react';
import X from 'lucide-react/dist/esm/icons/x.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Star from 'lucide-react/dist/esm/icons/star.js';
import ImageOff from 'lucide-react/dist/esm/icons/image-off.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import { TIER_CONFIG } from './PrizeGallery';
import { getLocalizedPrizeField } from '../utils/prizeHelpers';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { parseDrawDate, getDrawIanaTimeZone } from '../utils/dateFormat';

/**
 * PrizeDetailModal
 *
 * Full-detail view of a single prize. Triggered from PrizeGallery card click.
 * Responsive: centered card on desktop, near-full-screen sheet on mobile.
 * Closes on ×, Escape, or backdrop click.
 */
export default function PrizeDetailModal({ prize, isOpen, onClose, onBuyTickets }) {
  const backdropRef = useRef(null);
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen || !prize) return null;

  const tier = TIER_CONFIG[prize.tier] || TIER_CONFIG.consolation;
  const TierIcon = tier.icon;

  const name = getLocalizedPrizeField(prize.name, language);
  const description = getLocalizedPrizeField(prize.description, language);

  const drawDateLabel = prize.draw_date
    ? formatModalDate(prize)
    : null;

  function formatModalDate(source) {
    const d = parseDrawDate(source);
    if (!d) return null;
    const tz = getDrawIanaTimeZone(source);
    const opts = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
    if (tz) opts.timeZone = tz;
    return d.toLocaleDateString(undefined, opts);
  }

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
    >
      {/* Modal card */}
      <div className="relative w-full sm:max-w-lg bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-slideUp max-h-[95vh] sm:max-h-[85vh] flex flex-col">

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 p-1.5 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">

          {/* Prize Image */}
          <div className="relative h-56 sm:h-64 bg-gray-100 dark:bg-gray-800">
            {prize.image_url ? (
              <img
                src={prize.image_url}
                alt={name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                <TierIcon className={`w-16 h-16 ${tier.iconColor} opacity-40`} />
                <ImageOff className="w-6 h-6 text-gray-300 dark:text-gray-600" />
              </div>
            )}
            {/* Gradient scrim */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

            {/* Tier badge */}
            <div className={`absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-gradient-to-r ${tier.gradient} text-white text-xs font-bold uppercase tracking-wider shadow-lg`}>
              {tier.label}
            </div>
          </div>

          {/* Content */}
          <div className="p-5 sm:p-6 space-y-4">
            {/* Name */}
            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white leading-tight">
              {name}
            </h2>

            {/* Value */}
            {prize.value != null && (
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${tier.bg} ${tier.border} border`}>
                <TierIcon className={`w-5 h-5 ${tier.iconColor}`} />
                <span className="text-2xl font-black text-gray-900 dark:text-white">
                  ${Number(prize.value).toLocaleString()}
                </span>
              </div>
            )}

            {/* Description */}
            {description && (
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {description}
              </p>
            )}

            {/* Draw date */}
            {drawDateLabel && (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>{t('components.drawLabel', 'Draw')}: {drawDateLabel}</span>
              </div>
            )}
          </div>
        </div>

        {/* Sticky CTA footer */}
        {onBuyTickets && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
            <button
              onClick={() => {
                onClose();
                onBuyTickets();
              }}
              className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r ${tier.gradient} text-white font-bold text-base shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all`}
            >
              <Ticket className="w-5 h-5" />
              Buy Tickets
            </button>
          </div>
        )}
      </div>

      {/* Animations (injected once via style tag) */}
      <style>{`
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(2rem); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-fadeIn  { animation: fadeIn  0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.25s ease-out; }
      `}</style>
    </div>
  );
}
